import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    navigate("/admin", { replace: true });
    return null;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="grid min-h-screen bg-[#050505] lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src="/brand/valentina-bw.jpg" alt="Valentina Mishra" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-[#050505]" />
        <div className="absolute bottom-16 left-12">
          <Crown className="h-8 w-8 text-[#D4AF37]" strokeWidth={1} />
          <div className="font-script mt-4 text-5xl text-[#D4AF37]">D'La Valentina</div>
          <div className="mt-2 text-xs uppercase tracking-[0.35em] text-neutral-400">The Royal Court — Admin</div>
        </div>
      </div>
      <div className="flex items-center justify-center px-6">
        <form data-testid="admin-login-form" onSubmit={submit} className="w-full max-w-md border border-[#D4AF37]/20 bg-[#0C0C0C] p-10">
          <Crown className="h-8 w-8 text-[#D4AF37]" strokeWidth={1} />
          <h1 className="font-display mt-6 text-4xl font-medium text-white">Welcome Back</h1>
          <p className="mt-2 text-sm font-light text-neutral-400">Sign in to manage the house of D'La Valentina.</p>
          {error && (
            <div data-testid="admin-login-error" className="mt-6 border border-red-900/50 bg-red-950/30 px-4 py-3 text-sm text-red-300">
              {error}
            </div>
          )}
          <label className="mt-8 block text-[10px] uppercase tracking-[0.3em] text-neutral-400">Email</label>
          <input
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-2 w-full border border-white/15 bg-black/40 px-4 py-3.5 text-sm text-white outline-none transition-colors focus:border-[#D4AF37]"
            placeholder="admin@dlavalentina.co.in"
          />
          <label className="mt-6 block text-[10px] uppercase tracking-[0.3em] text-neutral-400">Password</label>
          <input
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2 w-full border border-white/15 bg-black/40 px-4 py-3.5 text-sm text-white outline-none transition-colors focus:border-[#D4AF37]"
            placeholder="••••••••••"
          />
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="mt-8 w-full bg-[#D4AF37] px-6 py-4 text-xs font-medium uppercase tracking-[0.3em] text-black transition-colors hover:bg-[#F9F1CC] disabled:opacity-50"
          >
            {loading ? "Entering..." : "Enter the Court"}
          </button>
        </form>
      </div>
    </div>
  );
}
